import React, { useEffect } from "react";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { z as zod } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { toast } from "@onair/minimal-ui/snackbar";
import { Field, Form } from "@onair/minimal-ui/hook-form";

import {
	useCreateLibraryMutation,
	useUpdateLibraryMutation,
} from "@onair/operations";

export type LibraryCreateSchemaType = zod.infer<typeof LibraryCreateSchema>;

export const LibraryCreateSchema = zod.object({
	name: zod
		.string()
		.min(1, { message: "Vui lòng nhập tên thư mục!" })
		.max(100, { message: "Vui lòng nhập tối đa 100 ký tự" }),
});

interface ICreateSubLibraryProps {
	id: string;
	subLibrary: { id: string | null; name: string | null };
	resetEditLibrary: () => void;
	open: boolean;
	onClose: () => void;
	refetch: () => void;
}

const CreateAndUpdateSubLibrary = (props: ICreateSubLibraryProps) => {
	const { id, subLibrary, resetEditLibrary, open, onClose, refetch } = props;

	const methods = useForm<LibraryCreateSchemaType>({
		mode: "all",
		resolver: zodResolver(LibraryCreateSchema),
		defaultValues: {
			name: "",
		},
	});

	const { handleSubmit, reset, setValue } = methods;

	useEffect(() => {
		if (subLibrary.id && subLibrary.name) {
			setValue("name", subLibrary.name);
		}
	}, [subLibrary]);

	const { mutateAsync: createLibrary } = useCreateLibraryMutation({
		onSuccess: () => {
			refetch();
			onCloseDialog();
			toast.success("Tạo thư mục thành công");
		},
		onError: () => {
			onCloseDialog();
			toast.error("Có lỗi xảy ra khi tạo thư mục");
		},
	});

	const { mutateAsync: updateLibrary } = useUpdateLibraryMutation({
		onSuccess: () => {
			refetch();
			onCloseDialog();
			toast.success("Cập nhật thư mục thành công");
		},
		onError: () => {
			onCloseDialog();
			toast.error("Có lỗi xảy ra khi chỉnh sửa thư mục");
		},
	});

	const onHandleSubmit = handleSubmit(async (values) => {
		try {
			if (subLibrary.id) {
				await updateLibrary({ id: subLibrary.id, ...values });
			} else {
				await createLibrary({ id, ...values });
			}
		} catch (error) {
			console.error(error);
		}
	});

	const onCloseDialog = () => {
		reset();
		onClose();
		resetEditLibrary();
	};

	return (
		<Dialog
			open={open}
			onClose={onCloseDialog}
			maxWidth="md"
			fullWidth
			aria-labelledby="sub-library-dialog-title"
			aria-describedby="sub-library-dialog-description"
		>
			<Form methods={methods} onSubmit={onHandleSubmit}>
				<DialogTitle id="alert-dialog-title">
					{subLibrary.id ? "Cập nhật thư mục" : "Tạo thư mục"}
				</DialogTitle>

				<DialogContent>
					<Field.Text name="name" placeholder="Tên thư mục" autoFocus />
				</DialogContent>

				<DialogActions>
					<Stack direction="row" spacing={1}>
						<Button onClick={onCloseDialog} variant="outlined">
							Đóng
						</Button>
						<Button color="primary" variant="contained" type="submit">
							{subLibrary.id ? "Cập nhật" : "Tạo"}
						</Button>
					</Stack>
				</DialogActions>
			</Form>
		</Dialog>
	);
};
export default CreateAndUpdateSubLibrary;
